/**
 * Video Effects Panel
 * Drawer panel for selecting video effects (blur, backgrounds)
 * Changes apply in real-time to the main video - use Cancel to revert
 */

import { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import { useVideoEffectsStore } from '../../contexts/videoEffectsStore';
import BackgroundGallery from './BackgroundGallery';

export default function VideoEffectsPanel({ open, onClose }) {
  const {
    effectType,
    blurLevel,
    selectedBackground,
    setEffectType,
    setBlurLevel,
    setSelectedBackground,
    isSupported,
  } = useVideoEffectsStore();

  // Store original settings when panel opens
  const originalSettingsRef = useRef(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Save settings when panel opens
  useEffect(() => {
    if (open) {
      originalSettingsRef.current = {
        effectType,
        blurLevel,
        selectedBackground,
      };
      setHasChanges(false);
    } else {
      originalSettingsRef.current = null;
      setHasChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Track changes
  useEffect(() => {
    if (open && originalSettingsRef.current) {
      const orig = originalSettingsRef.current;
      const changed =
        effectType !== orig.effectType ||
        blurLevel !== orig.blurLevel ||
        selectedBackground !== orig.selectedBackground;
      setHasChanges(changed);
    }
  }, [open, effectType, blurLevel, selectedBackground]);

  // Handle cancel - restore original settings
  const handleCancel = () => {
    if (originalSettingsRef.current) {
      const orig = originalSettingsRef.current;
      setEffectType(orig.effectType);
      setBlurLevel(orig.blurLevel);
      setSelectedBackground(orig.selectedBackground);
    }
    onClose();
  };

  // Handle apply - just close
  const handleApply = () => {
    onClose();
  };

  if (!isSupported) {
    return (
      <Drawer anchor="right" open={open} onClose={handleCancel}>
        <Box sx={{ width: 320, p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Video Effects</Typography>
            <IconButton onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Alert severity="warning">
            Video effects require WebGL. Your browser or device may not support this feature.
          </Alert>
        </Box>
      </Drawer>
    );
  }

  const blurLevels = [
    { value: 'light', label: 'Light' },
    { value: 'medium', label: 'Medium' },
    { value: 'heavy', label: 'Heavy' },
  ];

  return (
    <Drawer anchor="right" open={open} onClose={handleCancel}>
      <Box sx={{ width: 320, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Video Effects
          </Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Effect type toggle */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Effect Type
          </Typography>
          <ToggleButtonGroup
            value={effectType}
            exclusive
            onChange={(_, value) => value && setEffectType(value)}
            fullWidth
            size="small"
          >
            <ToggleButton value="none">
              <BlockIcon sx={{ mr: 0.5 }} />
              None
            </ToggleButton>
            <ToggleButton value="blur">
              <BlurOnIcon sx={{ mr: 0.5 }} />
              Blur
            </ToggleButton>
            <ToggleButton value="background">
              <WallpaperIcon sx={{ mr: 0.5 }} />
              Image
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* Content based on effect type */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {effectType === 'none' && (
            <Typography variant="body2" color="text.secondary">
              No effect applied. Your original camera feed is shown.
            </Typography>
          )}

          {effectType === 'blur' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Blur Intensity
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {blurLevels.map((level) => (
                  <Button
                    key={level.value}
                    variant={blurLevel === level.value ? 'contained' : 'outlined'}
                    onClick={() => setBlurLevel(level.value)}
                    fullWidth
                    size="small"
                  >
                    {level.label}
                  </Button>
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Blur hides your background while keeping you in focus.
              </Typography>
            </Box>
          )}

          {effectType === 'background' && <BackgroundGallery />}
        </Box>

        {/* Action buttons */}
        <Divider />
        <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UndoIcon />}
            onClick={handleCancel}
            fullWidth
            disabled={!hasChanges}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={handleApply}
            fullWidth
            color="primary"
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
